import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  GitBranch, ArrowDownRight, ArrowUpRight, Search, Info, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

/**
 * FailureTraceback — walk the trace graph from any entity in the workspace
 * to its full upstream + downstream neighbourhood. Answers "if X fails,
 * what's impacted?" without needing to know the exact IDs beforehand.
 *
 * Backend contract: GET /api/traceability/traceback/:type/:id?depth=N
 * returns { seed, nodes, edges, upstream_direct, downstream_direct, counts }.
 * Assistive-only — link presence does not prove compliance.
 */
const ENTITY_TYPES = [
  "requirement", "risk", "control", "verification", "document", "change",
  "capa", "deviation", "device", "training", "test_case", "submission_section",
  "clinical_evidence", "threat", "sbom", "iec62304_evidence", "cer",
  "adverse_event", "udi_record", "fria", "ivdr_evidence", "nis2_measure",
  "pccp", "model_version", "ai_control", "patch_plan", "iec62366_evidence",
];

const TYPE_COLORS = {
  requirement:  "bg-purple-100 text-purple-800 border-purple-200",
  risk:         "bg-red-100 text-red-800 border-red-200",
  control:      "bg-emerald-100 text-emerald-800 border-emerald-200",
  verification: "bg-sky-100 text-sky-800 border-sky-200",
  document:     "bg-slate-100 text-slate-700 border-slate-200",
  change:       "bg-amber-100 text-amber-800 border-amber-200",
  capa:         "bg-rose-100 text-rose-800 border-rose-200",
  deviation:    "bg-orange-100 text-orange-800 border-orange-200",
  device:       "bg-blue-100 text-blue-800 border-blue-200",
};

const RELATION_TONE = {
  mitigates:    "text-emerald-700",
  verifies:     "text-sky-700",
  implements:   "text-purple-700",
  evidences:    "text-slate-700",
  references:   "text-slate-500",
  derives_from: "text-purple-600",
  closes:       "text-emerald-600",
  impacts:      "text-red-700",
};

export default function FailureTraceback() {
  const [pickerType, setPickerType] = useState("risk");
  const [pickerId, setPickerId] = useState("");
  const [depth, setDepth] = useState(3);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);

  const [suggestions, setSuggestions] = useState({ requirements: [], risks: [], controls: [], verifications: [] });

  // Load a lightweight ID pool from the four canonical collections so the
  // picker can offer a datalist without a real search endpoint. Best-effort;
  // an empty list just means the user types the ID manually.
  useEffect(() => {
    (async () => {
      try {
        const [reqs, risks, controls, vers] = await Promise.allSettled([
          api.get("/requirements"),
          api.get("/risks"),
          api.get("/controls"),
          api.get("/verifications"),
        ]);
        setSuggestions({
          requirements:  reqs.status === "fulfilled" ? reqs.value.data.slice(0, 50) : [],
          risks:         risks.status === "fulfilled" ? risks.value.data.slice(0, 50) : [],
          controls:      controls.status === "fulfilled" ? controls.value.data.slice(0, 50) : [],
          verifications: vers.status === "fulfilled" ? vers.value.data.slice(0, 50) : [],
        });
      } catch (e) {
        logger.warn("suggestion load:", e?.response?.status);
      }
    })();
  }, []);

  const suggestList = useMemo(() => {
    const map = {
      requirement:  { list: suggestions.requirements,  key: "requirement_id",  labelKey: "title" },
      risk:         { list: suggestions.risks,         key: "risk_id",         labelKey: "title" },
      control:      { list: suggestions.controls,      key: "control_id",      labelKey: "title" },
      verification: { list: suggestions.verifications, key: "verification_id", labelKey: "title" },
    }[pickerType];
    if (!map) return [];
    return map.list.map((r) => ({ id: r[map.key], label: r[map.labelKey] || r[map.key] }));
  }, [pickerType, suggestions]);

  const runTrace = async () => {
    if (!pickerId.trim()) {
      toast.error("Enter or pick an entity ID first");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(
        `/traceability/traceback/${pickerType}/${encodeURIComponent(pickerId.trim())}?depth=${depth}`,
      );
      setGraph(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Traceback failed");
      setGraph(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="failure-traceback-page">
        <div>
          <div className="vq-overline">Traceability</div>
          <h1 className="font-display font-bold text-3xl text-slate-900 mt-1">
            <GitBranch className="inline h-7 w-7 mr-2 text-vq-blue" />
            Failure traceback
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Pick any requirement, risk, control, verification, document, change or CAPA and see everything it
            connects to — both what impacts it (upstream) and what it impacts (downstream). Assistive
            view only; link presence does not by itself demonstrate verification adequacy.
          </p>
        </div>

        {/* Picker */}
        <div className="vq-card p-4" data-testid="traceback-picker">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Entity type</Label>
              <select
                value={pickerType}
                onChange={(e) => setPickerType(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="traceback-type"
              >
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Entity ID</Label>
              <Input
                list={suggestList.length ? "traceback-suggestions" : undefined}
                placeholder={{ requirement: "req_...", risk: "rsk_...", control: "ctrl_...",
                                verification: "ver_..." }[pickerType] || "e.g. doc_..."}
                value={pickerId}
                onChange={(e) => setPickerId(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") runTrace(); }}
                className="mt-1 font-mono text-sm"
                data-testid="traceback-id"
              />
              {suggestList.length > 0 && (
                <datalist id="traceback-suggestions">
                  {suggestList.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </datalist>
              )}
            </div>
            <div>
              <Label className="text-xs">Depth</Label>
              <select
                value={depth}
                onChange={(e) => setDepth(Number(e.target.value))}
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
                data-testid="traceback-depth"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} hop{n === 1 ? "" : "s"}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-slate-500">
              <Info className="inline h-3 w-3 mr-1" />
              Tip: start with a Risk to see which requirements it derives from and which verifications close it.
            </p>
            <Button
              onClick={runTrace}
              disabled={loading || !pickerId.trim()}
              className="bg-vq-blue text-white hover:bg-vq-blue/90"
              data-testid="traceback-run"
            >
              <Search className="h-4 w-4 mr-1.5" />
              {loading ? "Tracing…" : "Run traceback"}
            </Button>
          </div>
        </div>

        {/* Results */}
        {graph && (
          <div className="space-y-4" data-testid="traceback-results">
            <div className="vq-card p-4 flex items-center gap-4 flex-wrap">
              <div>
                <div className="vq-overline">Seed</div>
                <div className="font-display font-bold text-slate-900">{graph.seed?.label}</div>
                <div className="text-xs font-mono text-slate-400">{graph.seed?.type}:{graph.seed?.id}</div>
              </div>
              <div className="ml-auto flex items-center gap-3 text-xs">
                <StatChip label="Nodes"  value={graph.counts?.nodes} />
                <StatChip label="Edges"  value={graph.counts?.edges} />
                <StatChip label="Direct upstream"   value={graph.counts?.upstream_direct} />
                <StatChip label="Direct downstream" value={graph.counts?.downstream_direct} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <TracebackList
                title="Upstream — what impacts this"
                icon={ArrowUpRight}
                items={graph.upstream_direct}
                testid="traceback-upstream"
              />
              <TracebackList
                title="Downstream — what this impacts"
                icon={ArrowDownRight}
                items={graph.downstream_direct}
                testid="traceback-downstream"
              />
            </div>

            <div className="vq-card p-4" data-testid="traceback-full-graph">
              <div className="vq-overline">Full graph (depth {graph.depth})</div>
              <div className="font-display font-bold text-slate-900 mt-1">
                {graph.nodes.length} node{graph.nodes.length === 1 ? "" : "s"} · {graph.edges.length} edge{graph.edges.length === 1 ? "" : "s"}
              </div>
              {graph.edges.length === 0 ? (
                <div className="text-sm text-slate-500 mt-2" data-testid="traceback-empty-graph">
                  No links from this entity yet. Add traces from the Traceability page to build the graph.
                </div>
              ) : (
                <div className="mt-3 space-y-1.5 max-h-96 overflow-auto pr-1">
                  {graph.edges.map((e, i) => {
                    const from = graph.nodes.find((n) => `${n.type}:${n.id}` === e.from);
                    const to   = graph.nodes.find((n) => `${n.type}:${n.id}` === e.to);
                    return (
                      <div
                        key={e.trace_id || `e-${i}`}
                        className="flex items-center gap-2 text-xs font-mono py-1.5 px-2 rounded hover:bg-slate-50"
                        data-testid={`traceback-edge-${i}`}
                      >
                        <EntityChip node={from} />
                        <span className={`italic ${RELATION_TONE[e.relation] || "text-slate-500"}`}>
                          — {e.relation} →
                        </span>
                        <EntityChip node={to} />
                        <span className="ml-auto text-[10px] text-slate-400 uppercase">
                          {e.direction}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-3">
                Assistive graph — link presence does not prove verification adequacy or regulatory compliance.
              </p>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function StatChip({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-lg font-display font-bold text-slate-900">{value ?? 0}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function EntityChip({ node }) {
  if (!node) return <span className="text-slate-400">—</span>;
  const cls = TYPE_COLORS[node.type] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`inline-flex items-center gap-1 border rounded-md px-1.5 py-0.5 ${cls}`}
      title={`${node.type}:${node.id}`}
    >
      <span className="text-[9px] uppercase tracking-wider opacity-70">{node.type}</span>
      <span className="font-sans truncate max-w-[220px]">{node.label}</span>
    </span>
  );
}

function TracebackList({ title, icon: Icon, items, testid }) {
  return (
    <div className="vq-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-vq-blue" />
        <div className="font-display font-bold text-slate-900">{title}</div>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-sm text-slate-500 mt-2" data-testid={`${testid}-empty`}>
          Nothing linked yet.
        </div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {items.map((item, i) => (
            <li
              key={`${item.node.type}:${item.node.id}-${i}`}
              className="flex items-center gap-2 text-xs py-1"
            >
              <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
              <Badge className="text-[9px] uppercase" variant="outline">{item.relation}</Badge>
              <EntityChip node={item.node} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
